import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '@/services/api';

/**
 * Defensive probe: returns `false` only when the follow-ups feature
 * is *known* to be unavailable (404 / 501). Default is `true` so the
 * sidebar shows the link unless we have positive evidence the backend
 * has the endpoint disabled.
 *
 * Cached aggressively (5 min) to avoid hammering the API on every
 * mount of the sidebar. Audit reference: H-4 / C-3 — when
 * `settings.FOLLOW_UPS_V2=False` in production the board endpoint
 * returns 404 and the sidebar item used to be a dead link.
 */
export function useFollowUpsAvailable(): boolean {
  const { data } = useQuery({
    queryKey: ['follow-ups', 'available'],
    queryFn: async () => {
      try {
        await api.get('/crm/follow-ups/board', {
          params: { items_per_column: 1 },
        });
        return true;
      } catch (err) {
        const status = (err as AxiosError)?.response?.status;
        if (status === 404 || status === 501) {
          return false;
        }
        // Auth / 5xx / network errors don't tell us the feature is
        // disabled — fail open so the link stays visible.
        return true;
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: true,
  });
  return data ?? true;
}
